import { FileTypeParser } from 'file-type';
import { detectXml } from '@file-type/xml';

const getAcceptTokens = (accept: string): string[] =>
    accept
        .split(',')
        .map((type) => type.trim().toLowerCase())
        .filter(Boolean);

const extensionAliases: Record<string, string> = {
    jpg: 'jpeg',
    tif: 'tiff'
};

const normalizeExtension = (extension: string): string => extensionAliases[extension] || extension;

const extensionToMimeMap: Record<string, string[]> = {
    png: ['image/png'],
    jpeg: ['image/jpeg'],
    webp: ['image/webp'],
    svg: ['image/svg+xml'],
    pdf: ['application/pdf'],
    eps: ['application/eps', 'application/postscript'],
    zip: ['application/zip'],
    csv: ['text/csv'],
    ttf: ['font/ttf', 'font/sfnt', 'application/font-sfnt'],
    otf: ['font/otf']
};

const getAllowedMimeTypes = (accept: string): string[] => {
    const explicitMimes = getAcceptTokens(accept).filter((type) => type.includes('/'));
    const extensionMimes = getAcceptTokens(accept)
        .filter((type) => type.startsWith('.'))
        .map((type) => normalizeExtension(type.replace(/^\./, '')))
        .map((extension) => extensionToMimeMap[extension])
        .filter(Boolean)
        .flat();

    return [...new Set([...explicitMimes, ...extensionMimes])];
};

const mimeTypeMatches = (detectedMime: string, acceptedMime: string): boolean => {
    if (acceptedMime.endsWith('/*')) {
        const acceptedPrefix = acceptedMime.slice(0, acceptedMime.indexOf('/') + 1);
        return detectedMime.startsWith(acceptedPrefix);
    }

    return detectedMime === acceptedMime;
};

const createParser = () => new FileTypeParser({ customDetectors: [detectXml] });

export const isValidFileSignature = async (file: File, accept: string): Promise<boolean> => {
    const acceptTokens = getAcceptTokens(accept);
    const allowedMimeTypes = getAllowedMimeTypes(accept);

    if (!acceptTokens.length || accept == '*') return true;
    if (!allowedMimeTypes.length) return false;
    if (!(file instanceof File)) return false;

    try {
        const detectedType = await createParser().fromBlob(file);
        if (!detectedType) {
            return false;
        }

        const detectedMime = detectedType.mime.toLowerCase();

        return allowedMimeTypes.some((acceptedMime) => mimeTypeMatches(detectedMime, acceptedMime));
    } catch {
        return false;
    }
};

/**
 * Accept string for the image-upload flows that historically only validated the
 * declared `format` string on the server (ImagesController.ImageUploadSignedUrl),
 * never the bytes. These are the image formats the backend already permits.
 */
export const IMAGE_UPLOAD_ACCEPT = '.png,.jpg,.jpeg,.webp,.svg';

/**
 * Detect the real type of a file by its signature (magic bytes) and return it as
 * a short label (e.g. "psd", "tiff", "png") for diagnostics, or `null` if the
 * type can't be determined. Framework-agnostic: no UI side effects.
 */
export const detectFileType = async (file: File): Promise<string | null> => {
    try {
        const detected = await createParser().fromBlob(file);
        return detected?.ext ?? null;
    } catch {
        return null;
    }
};

/**
 * Split a list of files into the ones whose real signature matches `accept` and
 * a count of the rejected ones. No UI side effects, so it can be reused from the
 * Vue and the AngularJS bundles alike (both share this module).
 */
export const partitionFilesBySignature = async (
    files: Iterable<File>,
    accept: string
): Promise<{ valid: File[]; invalidCount: number }> => {
    const valid: File[] = [];
    let invalidCount = 0;

    for (const file of files) {
        if (await isValidFileSignature(file, accept)) {
            valid.push(file);
        } else {
            invalidCount += 1;
        }
    }

    return { valid, invalidCount };
};
